// Byte exact, resumable reading of a log file.
//
// Chunks are always cut on a newline byte, which gives two things for free: the
// stored offset is always a line boundary (so a resume never re-reads or skips a
// line), and a chunk can be decoded on its own because `\n` never splits a UTF-8
// sequence. Any trailing partial line simply stays unread until the game writes
// the rest of it.

export const DEFAULT_CHUNK_SIZE = 4 * 1024 * 1024
const SIGNATURE_LENGTH = 1024

function fnv1a(bytes) {
    let hash = 0x811c9dc5
    for (let index = 0; index < bytes.length; index += 1) {
        hash ^= bytes[index]
        hash = Math.imul(hash, 0x01000193)
    }
    return (hash >>> 0).toString(36)
}

async function readBytes(file, start, end) {
    const from = Math.max(0, start)
    const to = Math.min(file.size, end)
    if (to <= from) return new Uint8Array(0)
    const buffer = await file.slice(from, to).arrayBuffer()
    return new Uint8Array(buffer)
}

export async function computeHeadSignature(file) {
    const bytes = await readBytes(file, 0, SIGNATURE_LENGTH)
    return `${file.size >= SIGNATURE_LENGTH ? SIGNATURE_LENGTH : bytes.length}:${fnv1a(bytes)}`
}

// Hashes the bytes right before the resume offset. If the game truncated and
// rewrote the file to the same length, this is what catches it.
export async function computeTailSignature(file, offset) {
    if (!offset) return ''
    const start = Math.max(0, offset - SIGNATURE_LENGTH)
    const bytes = await readBytes(file, start, offset)
    return `${offset}:${bytes.length}:${fnv1a(bytes)}`
}

export function describeFileChange(record, file) {
    if (!record || !record.offset) return 'full'
    if (file.size < record.offset) return 'truncated'
    if (file.size === record.size && file.lastModified === record.lastModified) return 'unchanged'
    return 'append'
}

// Confirms that the bytes already processed are still the same bytes. Returns
// 'append' when the tail matches, 'reset' when the file was rewritten.
export async function verifyResume(record, file) {
    const [head, tail] = await Promise.all([
        computeHeadSignature(file),
        computeTailSignature(file, record.offset),
    ])

    if (record.headSignature && head !== record.headSignature) return { mode: 'reset', head, tail }
    if (record.tailSignature && tail !== record.tailSignature) return { mode: 'reset', head, tail }
    return { mode: 'append', head, tail }
}

export async function readLineChunks(file, options) {
    const {
        fromOffset = 0,
        chunkSize = DEFAULT_CHUNK_SIZE,
        onText,
        onProgress,
        shouldAbort,
    } = options

    const decoder = new TextDecoder('utf-8')
    const size = file.size
    let offset = fromOffset
    let currentChunk = chunkSize

    while (offset < size) {
        if (shouldAbort && shouldAbort()) break

        const end = Math.min(offset + currentChunk, size)
        const bytes = await readBytes(file, offset, end)
        if (!bytes.length) break

        let cut = -1
        for (let index = bytes.length - 1; index >= 0; index -= 1) {
            if (bytes[index] === 10) {
                cut = index
                break
            }
        }

        if (cut < 0) {
            // Either a single line longer than the chunk, or the file ends with
            // a partial line the game has not finished writing.
            if (end >= size) break
            currentChunk *= 2
            if (currentChunk > 64 * 1024 * 1024) {
                throw new Error('A single log line exceeds 64 MB; the file does not look like a combat log')
            }
            continue
        }

        currentChunk = chunkSize
        const text = decoder.decode(bytes.subarray(0, cut + 1))
        await onText(text)
        offset += cut + 1

        if (onProgress) onProgress(offset, size)
        // Yields to the worker event loop so cancel messages are processed.
        await Promise.resolve()
    }

    return offset
}
